import React, { useState, useEffect, useMemo } from 'react';
import { FaSearch, FaTrash, FaMinusCircle, FaPlusCircle, FaFileExcel } from 'react-icons/fa';
import styles from './styles/CajaDiaria.module.css';
import { useAuth } from '../../context/AuthContext';

interface Local {
    locales_id: number;
    nombre_local: string;
}

interface CajaEntry {
    tipo: 'venta' | 'salida' | 'entrada';
    id: string;
    fecha: string;
    monto: number;          // positivo = venta/entrada, negativo = salida
    vendedor: string;
    descripcion: string;
    observaciones: string;
    tipo_pedido: string;
    acumulado: number;
}

const TIPOS_SALIDA = [
    'Marketing',
    'Retiro Luli',
    'Retiro Ignacio',
    'Retiro Virginia',
];

const TIPOS_ENTRADA = [
    'Aporte de capital',
    'Reposicion de caja',
    'Cobro a cuenta',
    'Otro',
];

interface CajaDiariaProps {
    metodo?: 'Efectivo' | 'Transferencia' | 'Tarjetas' | 'QR';
}

const CajaDiaria: React.FC<CajaDiariaProps> = ({ metodo = 'Efectivo' }) => {
    const { user } = useAuth();
    const [locales, setLocales] = useState<Local[]>([]);
    const [selectedLocal, setSelectedLocal] = useState<number | 'GLOBAL' | null>(null);
    const [fecha, setFecha] = useState(new Date().toISOString().split('T')[0]);
    const [fechaHasta, setFechaHasta] = useState(new Date().toISOString().split('T')[0]);
    const [entries, setEntries] = useState<CajaEntry[]>([]);
    const [searchTerm, setSearchTerm] = useState('');
    const [loading, setLoading] = useState(false);
    const [saldosDia, setSaldosDia] = useState<{ saldo_inicial: number; saldo_final: number }>({ saldo_inicial: 0, saldo_final: 0 });
    // Resumen consolidado de todos los locales (pestaña GLOBAL)
    const [globalSummary, setGlobalSummary] = useState<{ saldoInicial: number; totalVentas: number; totalEntradas: number; totalSalidas: number; saldoFinal: number } | null>(null);
    const [loadingGlobal, setLoadingGlobal] = useState(false);

    const isGlobal = selectedLocal === 'GLOBAL';
    const [isEditingInitial, setIsEditingInitial] = useState(false);
    const [tempInitial, setTempInitial] = useState('0');

    // Form movimiento (salida o entrada)
    const [formMov, setFormMov] = useState<'salida' | 'entrada'>('salida');
    const [formTipo, setFormTipo] = useState(TIPOS_SALIDA[0]);
    const [formMonto, setFormMonto] = useState('');
    const [formObservaciones, setFormObservaciones] = useState('');
    const [formMsg, setFormMsg] = useState<{ type: 'ok' | 'err'; text: string } | null>(null);
    const [submitting, setSubmitting] = useState(false);

    const tiposActuales = formMov === 'salida' ? TIPOS_SALIDA : TIPOS_ENTRADA;

    const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000';

    useEffect(() => {
        fetch(`${API_URL}/locales`)
            .then(r => r.json())
            .then(data => {
                if (Array.isArray(data)) setLocales(data);
            })
            .catch(console.error);
    }, [API_URL]);

    const fetchCaja = async () => {
        if (!selectedLocal || selectedLocal === 'GLOBAL') return;
        setLoading(true);
        try {
            const params = new URLSearchParams({ 
                fecha, 
                fecha_hasta: fechaHasta,
                id_local: selectedLocal.toString(),
                metodo 
            });
            const res = await fetch(`${API_URL}/admin/caja/diaria?${params}`);
            if (res.ok) setEntries(await res.json());
        } catch (e) {
            console.error(e);
        } finally {
            setLoading(false);
        }
    };

    const fetchSaldosDia = async () => {
        if (!selectedLocal || selectedLocal === 'GLOBAL') return;
        try {
            const params = new URLSearchParams({ id_local: selectedLocal.toString(), fecha, metodo });
            const res = await fetch(`${API_URL}/admin/caja/saldos?${params}`);
            if (res.ok) {
                const data = await res.json();
                setSaldosDia(data);
                setTempInitial(data.saldo_inicial.toString());
            }
        } catch (e) {
            console.error(e);
        }
    };

    const saveSaldosDia = async (inicial: number) => {
        if (!selectedLocal || selectedLocal === 'GLOBAL') return;
        try {
            await fetch(`${API_URL}/admin/caja/saldos`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ id_local: selectedLocal, fecha, metodo, saldo_inicial: inicial, saldo_final: 0 })
            });
            fetchSaldosDia();
            fetchCaja();
        } catch (e) {
            console.error(e);
        }
    };

    // Consolida los 5 totales de TODOS los locales (pestaña GLOBAL).
    // Reutiliza los endpoints por-local sumando sus resultados.
    const fetchGlobal = async () => {
        if (locales.length === 0) return;
        setLoadingGlobal(true);
        try {
            const resultados = await Promise.all(locales.map(async (loc) => {
                const id = loc.locales_id.toString();
                const pCaja = new URLSearchParams({ fecha, fecha_hasta: fechaHasta, id_local: id, metodo });
                const pSaldo = new URLSearchParams({ id_local: id, fecha, metodo });
                const [resCaja, resSaldo] = await Promise.all([
                    fetch(`${API_URL}/admin/caja/diaria?${pCaja}`),
                    fetch(`${API_URL}/admin/caja/saldos?${pSaldo}`),
                ]);
                const entriesLoc: CajaEntry[] = resCaja.ok ? await resCaja.json() : [];
                const saldoLoc = resSaldo.ok ? await resSaldo.json() : { saldo_inicial: 0 };

                const ventas = entriesLoc.filter(e => e.tipo === 'venta').reduce((s, e) => s + e.monto, 0);
                const entradas = entriesLoc.filter(e => e.tipo === 'entrada').reduce((s, e) => s + e.monto, 0);
                const salidas = entriesLoc.filter(e => e.tipo === 'salida').reduce((s, e) => s + Math.abs(e.monto), 0);
                const saldoInicial = Number(saldoLoc.saldo_inicial) || 0;
                return { saldoInicial, ventas, entradas, salidas };
            }));

            const acc = resultados.reduce((a, r) => ({
                saldoInicial: a.saldoInicial + r.saldoInicial,
                totalVentas: a.totalVentas + r.ventas,
                totalEntradas: a.totalEntradas + r.entradas,
                totalSalidas: a.totalSalidas + r.salidas,
            }), { saldoInicial: 0, totalVentas: 0, totalEntradas: 0, totalSalidas: 0 });

            setGlobalSummary({
                ...acc,
                saldoFinal: acc.saldoInicial + acc.totalVentas + acc.totalEntradas - acc.totalSalidas,
            });
        } catch (e) {
            console.error(e);
        } finally {
            setLoadingGlobal(false);
        }
    };

    useEffect(() => {
        if (selectedLocal === 'GLOBAL') {
            fetchGlobal();
        } else if (selectedLocal) {
            fetchCaja();
            fetchSaldosDia();
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [selectedLocal, fecha, fechaHasta, locales]);

    // Limpiar la busqueda al cambiar de local
    useEffect(() => {
        setSearchTerm('');
    }, [selectedLocal]);

    const summary = useMemo(() => {
        const totalVentas = entries
            .filter(e => e.tipo === 'venta')
            .reduce((s, e) => s + e.monto, 0);
        const totalEntradas = entries
            .filter(e => e.tipo === 'entrada')
            .reduce((s, e) => s + e.monto, 0);
        const totalSalidas = entries
            .filter(e => e.tipo === 'salida')
            .reduce((s, e) => s + Math.abs(e.monto), 0);
        const saldoDia = totalVentas + totalEntradas - totalSalidas;
        const calculadoFinal = saldosDia.saldo_inicial + saldoDia;
        return { totalVentas, totalEntradas, totalSalidas, saldoDia, calculadoFinal };
    }, [entries, saldosDia.saldo_inicial]);

    // Filtra solo la tabla visible (vendedor, descripcion, observaciones, monto o Nro de orden).
    // Los totales del resumen siguen calculandose sobre TODOS los movimientos.
    const filteredEntries = useMemo(() => {
        const q = searchTerm.trim().toLowerCase();
        if (!q) return entries;
        return entries.filter(e =>
            (e.vendedor || '').toLowerCase().includes(q) ||
            (e.descripcion || '').toLowerCase().includes(q) ||
            (e.observaciones || '').toLowerCase().includes(q) ||
            Math.abs(e.monto).toString().includes(q) ||
            (e.id || '').toLowerCase().includes(q)
        );
    }, [entries, searchTerm]);

    const handleSubmitMovimiento = async (e: React.FormEvent) => {
        e.preventDefault();
        if (!selectedLocal || selectedLocal === 'GLOBAL' || !formMonto || parseFloat(formMonto) <= 0) {
            setFormMsg({ type: 'err', text: 'Completar todos los campos correctamente.' });
            return;
        }
        setSubmitting(true);
        const endpoint = formMov === 'salida' ? 'salida' : 'entrada';
        try {
            const res = await fetch(`${API_URL}/admin/caja/${endpoint}`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    tipo: formTipo,
                    monto: parseFloat(formMonto),
                    id_local: selectedLocal,
                    fecha,
                    vendedor: user || '',
                    observaciones: formObservaciones,
                }),
            });
            if (res.ok) {
                setFormMsg({ type: 'ok', text: formMov === 'salida' ? 'Salida registrada.' : 'Entrada registrada.' });
                setFormMonto('');
                setFormObservaciones('');
                fetchCaja();
            } else {
                setFormMsg({ type: 'err', text: 'Error al registrar movimiento.' });
            }
        } catch {
            setFormMsg({ type: 'err', text: 'Error de conexion.' });
        } finally {
            setSubmitting(false);
            setTimeout(() => setFormMsg(null), 3000);
        }
    };

    const handleDeleteMovimiento = async (entry: CajaEntry) => {
        const isEntrada = entry.tipo === 'entrada';
        const numId = entry.id.replace(isEntrada ? 'E' : 'S', '');
        const label = isEntrada ? 'entrada' : 'salida';
        if (!window.confirm(`Eliminar esta ${label} de caja?`)) return;
        try {
            const endpoint = isEntrada ? 'entrada' : 'salida';
            const res = await fetch(`${API_URL}/admin/caja/${endpoint}/${numId}`, { method: 'DELETE' });
            if (res.ok) {
                fetchCaja();
            }
        } catch (e) {
            console.error(e);
        }
    };

    const tipoBadgeClass = (e: CajaEntry): string => {
        if (e.tipo === 'salida') return styles['tipo-salida-badge'];
        if (e.tipo === 'entrada') return styles['tipo-entrada-badge'];
        const tp = (e.tipo_pedido || '').toLowerCase();
        if (tp === 'retiro') return styles['tipo-retiro'];
        if (tp === 'envio') return styles['tipo-envio'];
        return styles['tipo-inmediato'];
    };

    const tipoLabel = (e: CajaEntry): string => {
        if (e.tipo === 'salida') return 'SALIDA';
        if (e.tipo === 'entrada') return 'ENTRADA';
        return (e.tipo_pedido || 'Venta').toUpperCase();
    };

    const nombreLocal = isGlobal ? 'GLOBAL' : (locales.find(l => l.locales_id === selectedLocal)?.nombre_local || '');

    const exportToCSV = () => {
        if (filteredEntries.length === 0) return;
        const BOM = '\uFEFF';
        const showAcumulado = metodo === 'Efectivo';
        const headers = showAcumulado
            ? ['#', 'Tipo', 'Descripcion', 'Observaciones', 'Monto', 'Total Acumulado', 'Vendedor', 'Fecha']
            : ['#', 'Tipo', 'Descripcion', 'Observaciones', 'Monto', 'Vendedor', 'Fecha'];
        const rows = filteredEntries.map((e, idx) => {
            const base: (string | number)[] = [
                idx + 1,
                tipoLabel(e),
                `"${(e.descripcion || '').replace(/"/g, '""')}"`,
                `"${(e.observaciones || '').replace(/"/g, '""')}"`,
                e.tipo === 'salida' ? -Math.abs(e.monto) : e.monto,
            ];
            if (showAcumulado) base.push(e.acumulado);
            base.push(`"${(e.vendedor || '').replace(/"/g, '""')}"`, e.fecha);
            return base;
        });
        const csv = BOM + [headers.join(';'), ...rows.map(r => r.join(';'))].join('\n');
        const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `caja_${nombreLocal}_${fecha}${fechaHasta !== fecha ? `_al_${fechaHasta}` : ''}.csv`;
        a.click();
        URL.revokeObjectURL(url);
    };

    return (
        <div className={styles.container}>
            {/* Selector de local */}
            <div className={styles['local-selector']}>
                {locales.map(loc => (
                    <button
                        key={loc.locales_id}
                        className={`${styles['local-btn']} ${selectedLocal === loc.locales_id ? styles.active : ''}`}
                        onClick={() => setSelectedLocal(loc.locales_id)}
                    >
                        {loc.nombre_local.toUpperCase()}
                    </button>
                ))}
                <button
                    className={`${styles['local-btn']} ${isGlobal ? styles.active : ''}`}
                    onClick={() => setSelectedLocal('GLOBAL')}
                >
                    GLOBAL
                </button>
            </div>

            {!selectedLocal && (
                <div style={{ color: 'var(--light-color)', opacity: 0.5, padding: '40px', textAlign: 'center' }}>
                    Selecciona un local para ver su caja diaria.
                </div>
            )}

            {/* Vista GLOBAL: consolidado de todos los locales */}
            {isGlobal && (
                <>
                    <div className={styles['top-bar']}>
                        <div className={styles['filter-group']}>
                            <label>Desde</label>
                            <input
                                type="date"
                                className={styles['filter-input']}
                                value={fecha}
                                onChange={e => setFecha(e.target.value)}
                            />
                        </div>
                        <div className={styles['filter-group']}>
                            <label>Hasta</label>
                            <input
                                type="date"
                                className={styles['filter-input']}
                                value={fechaHasta}
                                onChange={e => setFechaHasta(e.target.value)}
                            />
                        </div>
                        <button className={styles['filter-btn']} onClick={fetchGlobal} disabled={loadingGlobal}>
                            <FaSearch />
                            {loadingGlobal ? 'Cargando...' : 'Actualizar'}
                        </button>
                    </div>

                    <div className={styles['summary-row']}>
                        {metodo === 'Efectivo' && (
                            <div className={styles['summary-card']} style={{ borderColor: '#FFD700' }}>
                                <div className={styles['summary-card-label']}>Saldo Inicial (Todos los locales)</div>
                                <div className={`${styles['summary-card-value']} ${styles.neutral}`}>
                                    ${(globalSummary?.saldoInicial ?? 0).toLocaleString('es-AR')}
                                </div>
                            </div>
                        )}

                        <div className={styles['summary-card']}>
                            <div className={styles['summary-card-label']}>{fecha === fechaHasta ? 'Ventas del Dia' : 'Ventas del Periodo'}</div>
                            <div className={`${styles['summary-card-value']} ${styles.positive}`}>
                                ${(globalSummary?.totalVentas ?? 0).toLocaleString('es-AR')}
                            </div>
                        </div>

                        {metodo === 'Efectivo' && (
                            <div className={styles['summary-card']}>
                                <div className={styles['summary-card-label']}>{fecha === fechaHasta ? 'Entradas del Dia' : 'Entradas del Periodo'}</div>
                                <div className={`${styles['summary-card-value']} ${styles.positive}`}>
                                    ${(globalSummary?.totalEntradas ?? 0).toLocaleString('es-AR')}
                                </div>
                            </div>
                        )}

                        <div className={styles['summary-card']}>
                            <div className={styles['summary-card-label']}>{fecha === fechaHasta ? 'Salidas del Dia' : 'Salidas del Periodo'}</div>
                            <div className={`${styles['summary-card-value']} ${styles.negative}`}>
                                ${(globalSummary?.totalSalidas ?? 0).toLocaleString('es-AR')}
                            </div>
                        </div>

                        <div className={styles['summary-card']} style={{ borderColor: '#4fc3f7' }}>
                            <div className={styles['summary-card-label']}>{fecha === fechaHasta ? 'Saldo Final Dia' : 'Saldo Final Periodo'}</div>
                            <div className={`${styles['summary-card-value']} ${(globalSummary?.saldoFinal ?? 0) >= 0 ? styles.positive : styles.negative}`}>
                                ${(globalSummary?.saldoFinal ?? 0).toLocaleString('es-AR')}
                            </div>
                        </div>
                    </div>
                </>
            )}

            {selectedLocal && !isGlobal && (
                <>
                    {/* Barra de fecha */}
                    <div className={styles['top-bar']}>
                        <div className={styles['filter-group']}>
                            <label>Desde</label>
                            <input
                                type="date"
                                className={styles['filter-input']}
                                value={fecha}
                                onChange={e => setFecha(e.target.value)}
                            />
                        </div>
                        <div className={styles['filter-group']}>
                            <label>Hasta</label>
                            <input
                                type="date"
                                className={styles['filter-input']}
                                value={fechaHasta}
                                onChange={e => setFechaHasta(e.target.value)}
                            />
                        </div>
                        <button className={styles['filter-btn']} onClick={fetchCaja} disabled={loading}>
                            <FaSearch />
                            {loading ? 'Cargando...' : 'Actualizar'}
                        </button>
                    </div>

                    {/* Resumen */}
                    <div className={styles['summary-row']}>
                        {metodo === 'Efectivo' && (
                            <div className={styles['summary-card']} style={{ borderColor: '#FFD700' }}>
                                <div className={styles['summary-card-label']}>Saldo Inicial</div>
                                {isEditingInitial ? (
                                    <div style={{ display: 'flex', gap: '5px', marginTop: '5px' }}>
                                        <input 
                                            type="number" 
                                            className={styles['filter-input']}
                                            style={{ width: '100px', padding: '2px 5px' }}
                                            value={tempInitial} 
                                            onChange={e => setTempInitial(e.target.value)} 
                                        />
                                        <button
                                            className={styles['filter-btn']}
                                            style={{ padding: '2px 8px' }}
                                            onClick={() => {
                                                const val = parseFloat(tempInitial) || 0;
                                                saveSaldosDia(val);
                                                setIsEditingInitial(false);
                                            }}
                                        >Ok</button>
                                    </div>
                                ) : (
                                    <div 
                                        className={`${styles['summary-card-value']} ${styles.neutral}`}
                                        onClick={() => setIsEditingInitial(true)}
                                        style={{ cursor: 'pointer' }}
                                        title="Click para editar saldo inicial"
                                    >
                                        ${saldosDia.saldo_inicial.toLocaleString('es-AR')}
                                    </div>
                                )}
                            </div>
                        )}

                        <div className={styles['summary-card']}>
                            <div className={styles['summary-card-label']}>{fecha === fechaHasta ? 'Ventas del Dia' : 'Ventas del Periodo'}</div>
                            <div className={`${styles['summary-card-value']} ${styles.positive}`}>
                                ${summary.totalVentas.toLocaleString('es-AR')}
                            </div>
                        </div>

                        {metodo === 'Efectivo' && (
                            <div className={styles['summary-card']}>
                                <div className={styles['summary-card-label']}>{fecha === fechaHasta ? 'Entradas del Dia' : 'Entradas del Periodo'}</div>
                                <div className={`${styles['summary-card-value']} ${styles.positive}`}>
                                    ${summary.totalEntradas.toLocaleString('es-AR')}
                                </div>
                            </div>
                        )}

                        {metodo === 'Efectivo' && (
                            <div className={styles['summary-card']}>
                                <div className={styles['summary-card-label']}>{fecha === fechaHasta ? 'Salidas del Dia' : 'Salidas del Periodo'}</div>
                                <div className={`${styles['summary-card-value']} ${styles.negative}`}>
                                    ${summary.totalSalidas.toLocaleString('es-AR')}
                                </div>
                            </div>
                        )}

                        {metodo === 'Efectivo' && (
                            <div className={styles['summary-card']} style={{ borderColor: '#4fc3f7' }}>
                                <div className={styles['summary-card-label']}>{fecha === fechaHasta ? 'Saldo Final Dia' : 'Saldo Final Periodo'}</div>
                                <div className={`${styles['summary-card-value']} ${summary.calculadoFinal >= 0 ? styles.positive : styles.negative}`}>
                                    ${summary.calculadoFinal.toLocaleString('es-AR')}
                                </div>
                            </div>
                        )}
                    </div>

                    {/* Layout tabla + form */}
                    <div className={metodo === 'Efectivo' ? styles.layout : styles['layout-no-form']}>
                        {/* Tabla */}
                        <div className={styles['table-container']}>
                            <div className={styles['table-header']}>
                                <h4 className={styles['table-title']}>{nombreLocal}</h4>
                                <div style={{ display: 'flex', alignItems: 'center', gap: '12px', flexWrap: 'wrap' }}>
                                    <div style={{ position: 'relative', display: 'flex', alignItems: 'center' }}>
                                        <FaSearch style={{ position: 'absolute', left: '8px', opacity: 0.5, fontSize: '0.8rem' }} />
                                        <input
                                            type="text"
                                            className={styles['filter-input']}
                                            value={searchTerm}
                                            onChange={e => setSearchTerm(e.target.value)}
                                            placeholder="Buscar vendedor, monto o N° orden..."
                                            style={{ paddingLeft: '28px', minWidth: '230px' }}
                                        />
                                    </div>
                                    <span className={styles['table-count']}>
                                        {searchTerm.trim()
                                            ? `${filteredEntries.length} de ${entries.length} registros`
                                            : `${entries.length} registros`}
                                    </span>
                                    <button
                                        onClick={exportToCSV}
                                        disabled={filteredEntries.length === 0}
                                        title="Exportar a Excel"
                                        style={{
                                            display: 'flex', alignItems: 'center', gap: '6px',
                                            background: '#1d6f42', color: '#fff', border: 'none',
                                            borderRadius: '6px', padding: '5px 12px', cursor: 'pointer',
                                            fontSize: '0.82rem', fontWeight: 600, opacity: filteredEntries.length === 0 ? 0.4 : 1,
                                        }}
                                    >
                                        <FaFileExcel /> Exportar
                                    </button>
                                </div>
                            </div>
                            <div className={styles['table-wrapper']}>
                                <table className={styles['caja-table']}>
                                    <thead>
                                        <tr>
                                            <th style={{ width: '32px' }}>#</th>
                                            <th>Tipo</th>
                                            <th>Descripcion</th>
                                            <th style={{ textAlign: 'right' }}>Monto</th>
                                            {metodo === 'Efectivo' && (
                                                <th style={{ textAlign: 'right' }}>Total Acumulado</th>
                                            )}
                                            <th>Vendedor</th>
                                            <th></th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {filteredEntries.length === 0 ? (
                                            <tr className={styles['empty-row']}>
                                                <td colSpan={metodo === 'Efectivo' ? 7 : 6}>
                                                    {searchTerm.trim()
                                                        ? 'No hay movimientos que coincidan con la busqueda.'
                                                        : 'No hay ventas en efectivo registradas para este dia.'}
                                                </td>
                                            </tr>
                                        ) : (
                                            filteredEntries.map((e, idx) => (
                                                <tr
                                                    key={e.id}
                                                    className={
                                                        e.tipo === 'salida' ? styles['row-salida']
                                                        : e.tipo === 'entrada' ? styles['row-entrada']
                                                        : styles['row-venta']
                                                    }
                                                >
                                                    <td className={styles['row-num']}>{idx + 1}</td>
                                                    <td>
                                                        <span className={`${styles['tipo-badge']} ${tipoBadgeClass(e)}`}>
                                                            {tipoLabel(e)}
                                                        </span>
                                                    </td>
                                                    <td className={e.tipo === 'salida' ? styles['desc-salida'] : ''}>
                                                        <div>{e.descripcion}</div>
                                                        {e.tipo === 'salida' && e.observaciones && (
                                                            <div className={styles['movement-observations']}>
                                                                Obs: {e.observaciones}
                                                            </div>
                                                        )}
                                                    </td>
                                                    <td style={{ textAlign: 'right' }}>
                                                        <span className={
                                                            e.tipo === 'salida' ? styles['monto-salida']
                                                            : e.tipo === 'entrada' ? styles['monto-entrada']
                                                            : styles['monto-venta']
                                                        }>
                                                            {e.tipo === 'salida' ? '-' : '+'}${Math.abs(e.monto).toLocaleString('es-AR')}
                                                        </span>
                                                    </td>
                                                    {metodo === 'Efectivo' && (
                                                        <td style={{ textAlign: 'right' }}>
                                                            <span
                                                                className={styles['acumulado-cell']}
                                                                style={{ color: e.acumulado >= 0 ? '#81c784' : '#e57373' }}
                                                            >
                                                                ${e.acumulado.toLocaleString('es-AR')}
                                                            </span>
                                                        </td>
                                                    )}
                                                    <td style={{ color: 'var(--light-color)', opacity: 0.8 }}>
                                                        {e.vendedor || <span style={{ opacity: 0.35 }}>—</span>}
                                                    </td>
                                                    <td>
                                                        {(e.tipo === 'salida' || e.tipo === 'entrada') && (
                                                            <button
                                                                className={styles['btn-delete-row']}
                                                                onClick={() => handleDeleteMovimiento(e)}
                                                                title={`Eliminar ${e.tipo}`}
                                                            >
                                                                <FaTrash />
                                                            </button>
                                                        )}
                                                    </td>
                                                </tr>
                                            ))
                                        )}
                                    </tbody>
                                </table>
                            </div>
                        </div>
                        
                        {/* Formulario de movimiento (Solo para Efectivo) */}
                        {metodo === 'Efectivo' && (
                            <div className={styles['form-card']}>
                                <div className={styles['form-card-header']}>
                                <h4 className={styles['form-card-title']}>
                                    {formMov === 'salida' ? <><FaMinusCircle /> Registrar Salida</> : <><FaPlusCircle /> Registrar Entrada</>}
                                </h4>
                            </div>
                            <form className={styles['form-body']} onSubmit={handleSubmitMovimiento}>
                                <div className={styles['mov-toggle']}>
                                    <button
                                        type="button"
                                        className={`${styles['mov-toggle-btn']} ${formMov === 'salida' ? styles['mov-toggle-active-salida'] : ''}`}
                                        onClick={() => { setFormMov('salida'); setFormTipo(TIPOS_SALIDA[0]); }}
                                    >
                                        <FaMinusCircle /> Salida
                                    </button>
                                    <button
                                        type="button"
                                        className={`${styles['mov-toggle-btn']} ${formMov === 'entrada' ? styles['mov-toggle-active-entrada'] : ''}`}
                                        onClick={() => { setFormMov('entrada'); setFormTipo(TIPOS_ENTRADA[0]); }}
                                    >
                                        <FaPlusCircle /> Entrada
                                    </button>
                                </div>
                                <div className={styles['form-field']}>
                                    <label>{formMov === 'salida' ? 'Tipo de Salida' : 'Tipo de Entrada'}</label>
                                    <select value={formTipo} onChange={e => setFormTipo(e.target.value)}>
                                        {tiposActuales.map(t => (
                                            <option key={t} value={t}>{t}</option>
                                        ))}
                                    </select>
                                </div>
                                <div className={styles['form-field']}>
                                    <label>Monto ($)</label>
                                    <input
                                        type="number"
                                        min="0.01"
                                        step="0.01"
                                        value={formMonto}
                                        onChange={e => setFormMonto(e.target.value)}
                                        placeholder="Ej: 5000"
                                        required
                                    />
                                </div>
                                <div className={styles['form-field']}>
                                    <label>Registrado por</label>
                                    <input
                                        type="text"
                                        value={user || ''}
                                        readOnly
                                        style={{ opacity: 0.6, cursor: 'default' }}
                                    />
                                </div>
                                <div className={styles['form-field']}>
                                    <label>Observaciones (opcional)</label>
                                    <input
                                        type="text"
                                        value={formObservaciones}
                                        onChange={e => setFormObservaciones(e.target.value)}
                                        placeholder="Notas adicionales..."
                                    />
                                </div>
                                <div className={styles['form-field']}>
                                    <label>Local</label>
                                    <input
                                        type="text"
                                        value={nombreLocal}
                                        disabled
                                        style={{ opacity: 0.6 }}
                                    />
                                </div>
                                <div className={styles['form-field']}>
                                    <label>Fecha</label>
                                    <input
                                        type="date"
                                        value={fecha}
                                        onChange={e => setFecha(e.target.value)}
                                    />
                                </div>
                                {formMsg && (
                                    <div className={formMsg.type === 'ok' ? styles['msg-success'] : styles['msg-error']}>
                                        {formMsg.text}
                                    </div>
                                )}
                                <button
                                    type="submit"
                                    className={`${styles['btn-submit']} ${formMov === 'entrada' ? styles['btn-submit-entrada'] : ''}`}
                                    disabled={submitting}
                                >
                                    {submitting ? 'Registrando...' : (formMov === 'salida' ? 'Registrar Salida' : 'Registrar Entrada')}
                                </button>
                            </form>
                        </div>
                        )}
                    </div>
                </>
            )}
        </div>
    );
};

export default CajaDiaria;
